import React, { useState } from "react";
import "./OutputReports.css";

/* ---------- parsing / formatting helpers ---------- */
const toNum = (s) => {
  if (s == null) return NaN;
  const str = String(s).trim();
  if (str === "" || str === "-" || str === "—") return NaN;
  const neg = /^\(.*\)$/.test(str) || str.trim().startsWith("-");
  const n = parseFloat(str.replace(/[()$,%\s]/g, ""));
  return isNaN(n) ? NaN : neg ? -Math.abs(n) : n;
};
const usd = (n) =>
  isNaN(n) ? "—" : (n < 0 ? "-$" : "$") + Math.round(Math.abs(n)).toLocaleString("en-US");

// Accounting-style delta vs the Default column. Lower rate/APR/payment is
// cheaper → shown in (parens), green. Higher → "+", red. (Sign only — no
// "better/worse for the borrower" editorializing.)
const acctDelta = (dv, kind) => {
  const thr = kind === "payment" ? 1 : 0.0005;
  if (isNaN(dv) || Math.abs(dv) < thr) return null;
  const good = dv < 0;
  const body =
    kind === "payment"
      ? "$" + Math.round(Math.abs(dv)).toLocaleString("en-US")
      : Math.abs(dv).toFixed(3);
  return { text: good ? `(${body})` : `+${body}`, cls: good ? "acct-pos" : "acct-neg" };
};
const fmtBool = (v) => {
  if (v === true || v === "true" || v === "TRUE" || v === "Yes" || v === "YES") return "Yes";
  if (v === false || v === "false" || v === "FALSE" || v === "No" || v === "NO") return "No";
  return v == null || v === "" ? "—" : String(v);
};

// Group the flat engine input rows into titled cards (matching the design draft:
// Product & request / Borrower / Property / 1st mortgage / …). Keyword-routed on
// the label; order matters — Borrower is tested before the generic 2nd-mortgage
// rule so "…Number of Borrowers" / "Borrower DTI before 2nd…" land in Borrower.
// A fallback "Other" bucket guarantees no input row is ever dropped.
const INPUT_GROUPS = [
  { title: "1st mortgage", test: (n) => /1st mortgage/i.test(n) },
  { title: "Borrower", test: (n) => /borrower|credit score|\bdti\b|income/i.test(n) },
  { title: "Property", test: (n) => /home value|property|number of units|occupancy|zip/i.test(n) },
  { title: "Product & request", test: (n) => /product|documentation|purpose|2nd mortgage|yield|\bprice\b|origination/i.test(n) },
];
function groupInputs(arr) {
  const buckets = INPUT_GROUPS.map((g) => ({ title: g.title, rows: [] }));
  const other = { title: "Other inputs", rows: [] };
  (Array.isArray(arr) ? arr : []).forEach((item) => {
    if (!item || item.name == null) return;
    const name = String(item.name);
    const gi = INPUT_GROUPS.findIndex((g) => g.test(name));
    (gi >= 0 ? buckets[gi] : other).rows.push(item);
  });
  return buckets.concat(other).filter((b) => b.rows.length > 0);
}

/* ---------- build enriched scenarios from a loanOptions block ---------- */
function buildBlock(lo) {
  if (!lo || !Array.isArray(lo.columns) || !lo.data) return null;
  const cols = lo.columns;
  const d = lo.data;
  const at = (key, i) => (d[key] && d[key][i] != null ? d[key][i] : "");
  const amtKey = d["HELOAN Amount"] ? "HELOAN Amount" : d["HELOC Amount"] ? "HELOC Amount" : null;
  const termKey = d["Loan Term (months)"]
    ? "Loan Term (months)"
    : d["Draw(IO)/Repay Term (months)"]
    ? "Draw(IO)/Repay Term (months)"
    : null;
  let defaultIdx = cols.findIndex((c) => /default/i.test(String(c)));
  if (defaultIdx < 0) defaultIdx = 3;

  const scen = cols.map((name, i) => {
    const amount = toNum(at(amtKey, i));
    const compPct = toNum(at("Broker Comp. (%)", i));
    const origPct = toNum(at("Orig. Fee (%)", i));
    const totalPct = toNum(at("Orig. Fee + Comp. (%)", i));
    const buydownPct = toNum(at("Buydown (%)", i));
    return {
      name,
      i,
      isDefault: i === defaultIdx,
      amount,
      rate: toNum(at("Interest Rate (%)", i)),
      apr: toNum(at("APR (%)", i)),
      payment: toNum(at("Monthly Payment", i)),
      ioPayment: toNum(at("IO Period Payment", i)),
      dti: at("DTI / Qual. DTI (HELOC) (%)", i),
      // Comp type is engine-computed PER OPTION (driven by property state, and
      // it genuinely splits mid-table in TN/IA), so it renders as a row —
      // never as one label for the whole quote.
      compType: String(at("BPC or LPC", i) || "").trim(),
      compPct,
      origPct,
      totalPct,
      buydownPct,
      compDollars: !isNaN(compPct) && !isNaN(amount) ? (compPct / 100) * amount : NaN,
      origDollars: !isNaN(origPct) && !isNaN(amount) ? (origPct / 100) * amount : NaN,
      totalDollars: !isNaN(totalPct) && !isNaN(amount) ? (totalPct / 100) * amount : NaN,
      buydownDollars:
        !isNaN(buydownPct) && !isNaN(amount) ? (buydownPct / 100) * amount : NaN,
      raw: {
        amount: at(amtKey, i),
        rate: at("Interest Rate (%)", i),
        apr: at("APR (%)", i),
        payment: at("Monthly Payment", i),
        ioPayment: at("IO Period Payment", i),
        savings: at("Life of Loan Savings", i),
        dti: at("DTI / Qual. DTI (HELOC) (%)", i),
        margin: at("HELOC Margin (%)", i),
        term: termKey ? at(termKey, i) : "",
        appraisal: at("Appraisal Required", i),
      },
    };
  });
  const def = scen[defaultIdx] || {};
  return { cols, defaultIdx, def, scen };
}

export function OutputReports(props) {
  const outputData = props.outputData || {};
  const [showDetail, setShowDetail] = useState(false);
  const [runIdCopied, setRunIdCopied] = useState(false);
  // Normalize to real arrays — a non-array engine response must never throw
  // .find/.map/.length during render (that blanks the page).
  const resultArr = Array.isArray(outputData.result) ? outputData.result : [];
  const inputArr = Array.isArray(outputData.input) ? outputData.input : [];

  const findBy = (arr, needle) => {
    const hit = (arr || []).find((r) => (r.name || "").toLowerCase().includes(needle.toLowerCase()));
    return hit ? hit.value : "";
  };
  const inVal = (n) => findBy(inputArr, n);
  const outVal = (n) => findBy(resultArr, n);
  const cellV = (v) => (v == null || v === "" ? "—" : v);

  const heloan = buildBlock(outputData.loanOptions);
  const heloc = buildBlock(outputData.loanOptions2);
  const runId = outVal("Run ID");
  const ltv = outVal("LTV");
  const cltv = outVal("CLTV");

  // Historical detection from SERVER truth: the submit path prefixes the run
  // id with "H" whenever the priced date differs from the run date. That
  // marker is the ONLY signal used here — comparing dates client-side false
  // flags normal evening runs (the engine box clock is UTC, so date_time
  // rolls to tomorrow at 8pm ET while the pricing date stays today).
  const pricingDate = outVal("Pricing Date");
  // Run-id marker letters (before the digits): X = internal exception run,
  // H = historical rate sheet. Server truth — set by the submit path.
  const marker = (String(runId).match(/^[A-Z]+/) || [""])[0];
  const isHistorical = marker.indexOf("H") !== -1;
  const isException = marker.indexOf("X") !== -1;
  // Named exception reasons, server-computed against this account's STANDARD
  // rules (see exception-reasons.service.js). Absent for pre-sidecar runs —
  // the banner then falls back to its generic text.
  const exc = outputData.exception || null;

  const copyRunId = () => {
    try {
      if (navigator.clipboard && runId) navigator.clipboard.writeText(String(runId));
      setRunIdCopied(true);
      setTimeout(() => setRunIdCopied(false), 1500);
    } catch (e) {
      /* clipboard unavailable — non-fatal */
    }
  };

  /* rate / apr / payment cell with accounting delta vs Default */
  const DeltaCell = ({ block, s, raw, kind }) => {
    let d = null;
    if (!s.isDefault) {
      if (kind === "rate" || kind === "apr") d = acctDelta(s[kind] - block.def[kind], kind);
      else if (kind === "payment") d = acctDelta(s.payment - block.def.payment, "payment");
    }
    return (
      <td className={s.isDefault ? "col-default num" : "num"}>
        {raw || "—"}
        {d && <span className={"lo-delta " + d.cls}>{d.text}</span>}
      </td>
    );
  };

  /* one product options table */
  const OptionsTable = ({ block, product, isHeloc }) => {
    if (!block) return null;
    const { scen, def } = block;
    const termRaw = (def.raw && def.raw.term) || "";
    let headerLine;
    if (isHeloc) {
      const parts = String(termRaw).replace(/\s/g, "").split("/");
      headerLine =
        parts.length >= 2 && parts[0] && parts[1]
          ? `variable rate · interest-only during ${parts[0]}-mo draw, then ${parts[1]}-mo repay`
          : "variable rate · interest-only during the draw, then amortizing";
    } else {
      const t = String(termRaw).replace(/\s/g, "");
      headerLine = `fixed rate · fully amortizing${t ? ` · ${t} months` : ""}`;
    }

    const th = (s) => (
      <th key={s.i} className={s.isDefault ? "col-default" : ""}>
        {s.name}
      </th>
    );
    const cell = (s, content, extraClass = "") => (
      <td key={s.i} className={(s.isDefault ? "col-default num " : "num ") + extraClass}>
        {content}
      </td>
    );
    const buydownCostCell = (s) => {
      if (isNaN(s.buydownPct) || s.buydownPct <= 0 || isNaN(s.buydownDollars)) return cell(s, "—");
      return cell(s, `${s.buydownPct.toFixed(2)}% · ${usd(s.buydownDollars)}`);
    };
    const breakEvenCell = (s) => {
      const monthlySavings = def.payment - s.payment; // positive when this option is cheaper
      if (isNaN(s.buydownDollars) || s.buydownDollars <= 0 || isNaN(monthlySavings) || monthlySavings <= 0)
        return cell(s, "—");
      return cell(s, `≈${Math.round(s.buydownDollars / monthlySavings)} mo`);
    };

    return (
      <div className="card shadow-sm output-card">
        <div className="card-body">
          <div className="lo-thead-line">
            <h5 className="output-h">{product}</h5>
            <span className="lo-subline">{headerLine}</span>
            <span className="lo-colkey">← more comp · lower rate →</span>
          </div>
          <div className="lo-scroll">
            <table className="lo2">
              <thead>
                <tr>
                  <th className="rowlab">Metric</th>
                  {scen.map(th)}
                </tr>
              </thead>
              <tbody>
                <tr className="emph">
                  <td className="rowlab">{product} amount</td>
                  {scen.map((s) => cell(s, s.raw.amount || "—"))}
                </tr>
                <tr className="emph">
                  <td className="rowlab">Interest rate</td>
                  {scen.map((s) => (
                    <DeltaCell key={s.i} block={block} s={s} raw={s.raw.rate} kind="rate" />
                  ))}
                </tr>
                {isHeloc && (
                  <tr>
                    <td className="rowlab">HELOC margin</td>
                    {scen.map((s) => cell(s, s.raw.margin || "—"))}
                  </tr>
                )}
                <tr>
                  <td className="rowlab">APR</td>
                  {scen.map((s) => (
                    <DeltaCell key={s.i} block={block} s={s} raw={s.raw.apr} kind="apr" />
                  ))}
                </tr>
                <tr>
                  <td className="rowlab">
                    Monthly payment
                    {isHeloc && <div className="lo-sub">repay period</div>}
                  </td>
                  {scen.map((s) => (
                    <DeltaCell key={s.i} block={block} s={s} raw={s.raw.payment} kind="payment" />
                  ))}
                </tr>
                {isHeloc && (
                  <tr>
                    <td className="rowlab">IO period payment</td>
                    {scen.map((s) => cell(s, s.raw.ioPayment || "—"))}
                  </tr>
                )}
                <tr>
                  <td className="rowlab">DTI (qualifying)</td>
                  {scen.map((s) => cell(s, s.dti || "—"))}
                </tr>
                <tr>
                  <td className="rowlab">Appraisal required</td>
                  {scen.map((s) => cell(s, fmtBool(s.raw.appraisal)))}
                </tr>
                <tr className="groupgap">
                  <td className="rowlab" />
                  {scen.map((s) => (
                    <td key={s.i} className={s.isDefault ? "col-default" : ""} />
                  ))}
                </tr>
                <tr>
                  <td className="rowlab">
                    Buydown cost
                    <div className="lo-sub">discount points · paid at closing</div>
                  </td>
                  {scen.map(buydownCostCell)}
                </tr>
                <tr>
                  <td className="rowlab">
                    Break-even
                    <div className="lo-sub">cost ÷ monthly savings</div>
                  </td>
                  {scen.map(breakEvenCell)}
                </tr>
                <tr>
                  <td className="rowlab">Origination fee</td>
                  {scen.map((s) =>
                    cell(s, isNaN(s.origDollars) ? "—" : `${s.origPct.toFixed(2)}% · ${usd(s.origDollars)}`)
                  )}
                </tr>
                <tr className="comp emph">
                  <td className="rowlab">Broker comp</td>
                  {scen.map((s) =>
                    cell(s, isNaN(s.compDollars) ? "—" : `${s.compPct.toFixed(2)}% · ${usd(s.compDollars)}`)
                  )}
                </tr>
                <tr>
                  <td className="rowlab">Total fee + comp</td>
                  {scen.map((s) =>
                    cell(s, isNaN(s.totalDollars) ? "—" : `${s.totalPct.toFixed(2)}% · ${usd(s.totalDollars)}`)
                  )}
                </tr>
                <tr>
                  <td className="rowlab">Comp type</td>
                  {scen.map((s) => cell(s, s.compType || "—"))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="lo-caption">
            Δ vs the Default column · green = lower rate/cost, red = higher.
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="output-report-wrap">
      {isHistorical && (
        <div className="lo-historical-banner">
          <span className="lo-historical-tag">HISTORICALLY PRICED</span>
          This quote uses the <b>{pricingDate || "backdated"}</b> rate sheet,
          not today&apos;s. Run id {runId ? <b>{runId}</b> : null} is marked
          accordingly.
        </div>
      )}
      {isException && exc && exc.noExceptionRequired ? (
        <div className="lo-exception-banner lo-exception-ok">
          <span className="lo-exception-tag">NO EXCEPTION REQUIRED</span>
          Exception pricing was on, but this quote is within this
          account&apos;s standard guidelines — it can be run without the flag.
        </div>
      ) : isException ? (
        <div className="lo-exception-banner">
          <span className="lo-exception-tag">INTERNAL EXCEPTION</span>
          {exc && exc.reasons && exc.reasons.length > 0 ? (
            <>
              Priced as an exception (internal only, use at your own risk).
              Reason{exc.reasons.length > 1 ? "s" : ""}, as read off the run
              id:
              <ul className="lo-exception-reasons">
                {exc.reasons.map((r, i) => (
                  <li key={r.code + "-" + i}>
                    <b>{r.code}</b> — {r.message}
                  </li>
                ))}
              </ul>
              {exc.note && <div className="lo-exception-note">{exc.note}</div>}
            </>
          ) : exc && exc.note ? (
            <>
              Priced with exception eligibility (internal only).
              <div className="lo-exception-note">{exc.note}</div>
            </>
          ) : (
            <>
              Priced with exception eligibility (internal only). Run id{" "}
              <b>{runId}</b> carries the X marker.
            </>
          )}
        </div>
      ) : null}
      {/* borrower context chips */}
      <div className="card shadow-sm output-card">
        <div className="card-body">
          <div className="lo-sumstrip">
            <div className="sc"><span className="sl">Qualifying FICO</span><span className="sv">{outVal("Qualifying Credit Score") || inVal("Borrower Credit Score") || "—"}</span></div>
            <div className="sc"><span className="sl">Home Value</span><span className="sv">{inVal("Home Value") || "—"}</span></div>
            <div className="sc"><span className="sl">LTV / CLTV</span><span className="sv">{cellV(ltv)} / {cellV(cltv)}</span></div>
            <div className="sc"><span className="sl">DTI before 2nd</span><span className="sv">{inVal("DTI before") || "—"}</span></div>
            <div className="sc"><span className="sl">Occupancy</span><span className="sv">{inVal("Occupancy") || "—"}</span></div>
            <div className="sc"><span className="sl">Purpose</span><span className="sv">{inVal("Loan Purpose") || "—"}</span></div>
          </div>
          {runId && (
            <div className="lo-runid">
              Run ID <b className={isHistorical ? "lo-runid-hist" : ""}>{runId}</b>
              <button type="button" className="lo-copy" onClick={copyRunId}>
                {runIdCopied ? "Copied ✓" : "Copy"}
              </button>
              {pricingDate && (
                <span className={"lo-pricing-date" + (isHistorical ? " lo-pricing-date-hist" : "")}>
                  Priced as of <b>{pricingDate}</b>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* HELOAN + HELOC option tables */}
      <OptionsTable block={heloan} product="HELOAN" isHeloc={false} />
      <OptionsTable block={heloc} product="HELOC" isHeloc={true} />

      {/* rate / comp note card */}
      <div className="lo-note">
        HELOC rate floor is 2% below the note rate. Button Finance may use BPC instead of LPC where state
        and federal regulations require it.
      </div>

      {/* full inputs & run details — toggle sits right above where it opens */}
      <div className="card shadow-sm output-card">
        <div className="card-body">
          <button type="button" className="lo-detail-toggle" onClick={() => setShowDetail((v) => !v)}>
            {showDetail ? "Hide full inputs & run details ▴" : "Show full inputs & run details ▾"}
          </button>
          {showDetail && (
            <div className="lo-detail-stack">
              <div>
                <h6 className="output-h">Loan Inputs</h6>
                <div className="lo-desc">Everything the engine received for this run, grouped.</div>
                <div className="lo-ingrid">
                  {groupInputs(inputArr).map((g) => (
                    <div className="lo-incard" key={g.title}>
                      <div className="lo-incard-h">{g.title}</div>
                      {g.rows.map((item, k) => (
                        <div className="lo-inrow" key={g.title + k}>
                          <span className="k">{item.name}</span>
                          <span className="v">{cellV(item.value)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h6 className="output-h">Loan Outputs</h6>
                <div className="lo-scroll">
                  <table className="lo2 qt">
                    <thead>
                      <tr>
                        <th className="rowlab">Metric</th>
                        <th>HELOAN</th>
                        <th>HELOC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultArr.map((item, k) => {
                        const hasBoth = item.value2 != null && item.value2 !== "";
                        return (
                          <tr key={"o" + k}>
                            <td className="rowlab">{item.name}</td>
                            {hasBoth ? (
                              <>
                                <td className="num">{cellV(item.value)}</td>
                                <td className="num">{cellV(item.value2)}</td>
                              </>
                            ) : (
                              <td className="num" colSpan={2}>{cellV(item.value)}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="lo-footnote">
        Indicate the loan option you'd like when you{" "}
        <a href="https://buttonfinance.encompasstpoconnect.com/#/content/login" target="_blank" rel="noreferrer">submit your loan</a>.
      </p>
    </div>
  );
}

export default OutputReports;
